import { createTheme } from '@mui/material/styles';

const sharedTypography = {
  fontFamily: 'Signika, sans-serif',
  h1: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.35 },
  h2: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
  body1: { fontSize: '1rem', lineHeight: 1.45 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
  button: { fontSize: '1rem', fontWeight: 600, textTransform: 'none' },
};

const sharedComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        minHeight: 52,
        boxShadow: 'none',
      },
      containedPrimary: {
        '&:hover': { boxShadow: 'none' },
      },
    },
  },
  MuiTextField: {
    defaultProps: { variant: 'outlined', size: 'medium' },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          minHeight: 52,
          borderRadius: 12,
        },
      },
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: { height: 8 },
      thumb: { width: 24, height: 24 },
    },
  },
};

/** @typedef {'L1' | 'L2' | 'L3' | 'L4' | 'D1' | 'D2' | 'D3' | 'D4' | 'D5' | 'D6'} MobileThemeVariantId */

export const LIGHT_THEME_VARIANTS = ['L1', 'L2', 'L3', 'L4'];
export const DARK_THEME_VARIANTS = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'];

export const DEFAULT_VARIANT_BY_MODE = {
  light: 'L1',
  dark: 'D1',
};

export const MOBILE_THEME_VARIANT_META = {
  L1: { mode: 'light', label: 'Lagoon', hint: 'Orange CTA + teal' },
  L2: { mode: 'light', label: 'Sunset', hint: 'Warm sand + orange' },
  L3: { mode: 'light', label: 'Granite', hint: 'Gray + teal CTA' },
  L4: { mode: 'light', label: 'Gold touch', hint: 'Orange + gold accent' },
  D1: { mode: 'dark', label: 'Twilight', hint: 'Deep sea + orange CTA' },
  D2: { mode: 'dark', label: 'Midnight rock', hint: 'Darkest + teal CTA' },
  D3: { mode: 'dark', label: 'Dusk bay', hint: 'Soft blue-grey + orange CTA' },
  D4: { mode: 'dark', label: 'Fog harbor', hint: 'Warm neutral grey + orange CTA' },
  D5: { mode: 'dark', label: 'Shallow water', hint: 'Teal-tinted dim + teal CTA' },
  D6: { mode: 'dark', label: 'Granite dusk', hint: 'Cool slate + teal CTA' },
};

const MOBILE_PALETTES = {
  L1: {
    mode: 'light',
    primary: { main: '#EE5A00', contrastText: '#ffffff' },
    secondary: { main: '#2b7a96', contrastText: '#ffffff' },
    background: { default: '#F7F9FA', paper: '#FFFFFF' },
    text: { primary: '#122652', secondary: '#5A6472' },
    divider: '#E8ECF0',
    mobile: { highlight: '#E8F6F8', watermark: '#122652' },
  },
  L2: {
    mode: 'light',
    primary: { main: '#D84300', contrastText: '#ffffff' },
    secondary: { main: '#2b7a96', contrastText: '#ffffff' },
    background: { default: '#FAF6F0', paper: '#FFFCF7' },
    text: { primary: '#3D2E1F', secondary: '#6B5E4F' },
    divider: '#EDE4D6',
    mobile: { highlight: '#FFF0E0', watermark: '#3D2E1F' },
  },
  L3: {
    mode: 'light',
    primary: { main: '#2b7a96', contrastText: '#ffffff' },
    secondary: { main: '#5A6472', contrastText: '#ffffff' },
    background: { default: '#F0F2F4', paper: '#FFFFFF' },
    text: { primary: '#1F2937', secondary: '#6B7280' },
    divider: '#E5E7EB',
    mobile: { highlight: '#E8ECF0', watermark: '#1F2937' },
  },
  L4: {
    mode: 'light',
    primary: { main: '#EE5A00', contrastText: '#ffffff' },
    secondary: { main: '#DAA520', contrastText: '#122652' },
    background: { default: '#F7F9FA', paper: '#FFFFFF' },
    text: { primary: '#122652', secondary: '#5A6472' },
    divider: '#E8ECF0',
    mobile: { highlight: '#FFF8E8', watermark: '#122652' },
  },
  D1: {
    mode: 'dark',
    primary: { main: '#F07830', contrastText: '#ffffff' },
    secondary: { main: '#01ddfa', contrastText: '#0F1A22' },
    background: { default: '#0F1A22', paper: '#1A2832' },
    text: { primary: '#E8F0F4', secondary: '#9AABB8' },
    divider: '#2A3D48',
    mobile: { highlight: '#1E3A45', watermark: '#a7dfe7' },
  },
  D2: {
    mode: 'dark',
    primary: { main: '#2b7a96', contrastText: '#ffffff' },
    secondary: { main: '#01ddfa', contrastText: '#0A1218' },
    background: { default: '#0A1218', paper: '#121E26' },
    text: { primary: '#E0E8EC', secondary: '#8A9AA6' },
    divider: '#1E2E38',
    mobile: { highlight: '#152530', watermark: '#a7dfe7' },
  },
  D3: {
    mode: 'dark',
    primary: { main: '#F07830', contrastText: '#ffffff' },
    secondary: { main: '#01ddfa', contrastText: '#2A3640' },
    background: { default: '#2A3640', paper: '#354552' },
    text: { primary: '#E8F0F4', secondary: '#A8B8C4' },
    divider: '#3F4F5C',
    mobile: { highlight: '#3A4D58', watermark: '#a7dfe7' },
  },
  D4: {
    mode: 'dark',
    primary: { main: '#EE5A00', contrastText: '#ffffff' },
    secondary: { main: '#4A9BB0', contrastText: '#ffffff' },
    background: { default: '#3A3F45', paper: '#464C54' },
    text: { primary: '#ECEFF2', secondary: '#B0B5BC' },
    divider: '#565C64',
    mobile: { highlight: '#4E545C', watermark: '#a7dfe7' },
  },
  D5: {
    mode: 'dark',
    primary: { main: '#3D9AAD', contrastText: '#ffffff' },
    secondary: { main: '#E87840', contrastText: '#ffffff' },
    background: { default: '#2F4A52', paper: '#3A5860' },
    text: { primary: '#E8F4F6', secondary: '#A8C0C8' },
    divider: '#456570',
    mobile: { highlight: '#3E6068', watermark: '#a7dfe7' },
  },
  D6: {
    mode: 'dark',
    primary: { main: '#2b7a96', contrastText: '#ffffff' },
    secondary: { main: '#8A9AA6', contrastText: '#454C58' },
    background: { default: '#454C58', paper: '#525A66' },
    text: { primary: '#EEF1F4', secondary: '#B8BEC8' },
    divider: '#606874',
    mobile: { highlight: '#5A6270', watermark: '#a7dfe7' },
  },
};

function hexToRgb(hex) {
  const value = hex.replace('#', '');
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function surfaceFromPaper(paper, mode) {
  const { r, g, b } = hexToRgb(paper);
  const alpha = mode === 'dark' ? 0.62 : 0.8;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function isMobileThemeVariantId(value) {
  return value != null && Object.prototype.hasOwnProperty.call(MOBILE_PALETTES, value);
}

export function normalizeMobileThemeVariantId(value, mode = 'light') {
  const id = typeof value === 'string' ? value.toUpperCase() : '';
  if (isMobileThemeVariantId(id)) {
    return id;
  }
  return DEFAULT_VARIANT_BY_MODE[mode === 'dark' ? 'dark' : 'light'];
}

export function getVariantsForMode(mode) {
  return mode === 'dark' ? DARK_THEME_VARIANTS : LIGHT_THEME_VARIANTS;
}

/**
 * @param {MobileThemeVariantId | string} variantId
 */
export function getMobileTheme(variantId) {
  const id = normalizeMobileThemeVariantId(variantId);
  const tokens = MOBILE_PALETTES[id];

  return createTheme({
    palette: {
      mode: tokens.mode,
      primary: tokens.primary,
      secondary: tokens.secondary,
      background: tokens.background,
      text: tokens.text,
      divider: tokens.divider,
      mobile: {
        ...tokens.mobile,
        surface: surfaceFromPaper(tokens.background.paper, tokens.mode),
        watermarkOpacity: tokens.mode === 'dark' ? 0.1 : 0.07,
      },
    },
    typography: sharedTypography,
    components: sharedComponents,
    shape: { borderRadius: 16 },
  });
}
